import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, useAuth } from "@/lib/auth";
import { useLang } from "@/lib/lang";
import { Users, Loader2, CheckCircle2 } from "lucide-react";

const PENDING_INVITE_KEY = "vivoz_pending_invite";

export default function JoinTeam() {
    const { code } = useParams();
    const { t } = useLang();
    const { user, loading: authLoading } = useAuth();
    const nav = useNavigate();
    const [status, setStatus] = useState("checking"); // checking | joining | done | error
    const [error, setError] = useState("");

    useEffect(() => {
        if (authLoading) return;

        if (!user) {
            // Remember the code across the login/register redirect so we
            // can finish the join once the user comes back authenticated.
            sessionStorage.setItem(PENDING_INVITE_KEY, code);
            setStatus("checking");
            return;
        }

        sessionStorage.removeItem(PENDING_INVITE_KEY);
        setStatus("joining");
        api.post(`/team/join/${code}`)
            .then(() => setStatus("done"))
            .catch((err) => {
                setError(err.response?.data?.detail || t("join_team.invalid"));
                setStatus("error");
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, authLoading, code]);

    return (
        <div className="min-h-screen bg-white text-slate-900">
            <Navbar />
            <div className="max-w-md mx-auto px-6 pt-24 pb-24 text-center">
                <div className="w-16 h-16 rounded-full bg-brand/10 flex items-center justify-center mx-auto mb-6">
                    {status === "done" ? (
                        <CheckCircle2 size={28} className="text-brand" />
                    ) : (
                        <Users size={28} className="text-brand" />
                    )}
                </div>

                {(status === "checking" || status === "joining") && (
                    <>
                        <div className="flex items-center justify-center gap-2 text-slate-500 mb-8" data-testid="join-team-loading">
                            <Loader2 size={16} className="animate-spin" />
                            {status === "joining" ? t("join_team.joining") : t("join_team.checking")}
                        </div>
                        {!user && !authLoading && (
                            <div className="flex flex-col gap-3">
                                <Link to="/login" className="btn-primary w-full justify-center" data-testid="join-team-login-btn">
                                    {t("join_team.login_cta")}
                                </Link>
                                <Link to="/register" className="btn-ghost w-full justify-center" data-testid="join-team-register-btn">
                                    {t("join_team.register_cta")}
                                </Link>
                            </div>
                        )}
                    </>
                )}

                {status === "done" && (
                    <>
                        <p className="text-slate-700 mb-8" data-testid="join-team-success">{t("join_team.success")}</p>
                        <button onClick={() => nav("/team")} className="btn-primary w-full justify-center" data-testid="join-team-go-btn">
                            {t("join_team.go_team")}
                        </button>
                    </>
                )}

                {status === "error" && (
                    <p className="text-slate-500" data-testid="join-team-error">{error}</p>
                )}
            </div>
        </div>
    );
}
